import { useMemo, useState, type DragEvent } from 'react';
import { Link } from 'react-router';
import { AlertTriangle, CalendarDays, ChevronLeft, ChevronRight, Download } from 'lucide-react';
import { addDays, type LocalDate } from '@habitos/engine';
import { useI18n } from '../i18n';
import { useApp } from '../state';
import { buildWeek, weekStartOf, type AgendaEntry } from '../domain/agenda';
import { buildIcs } from '../domain/ics';
import { toEngineSettings, type HabitRec } from '../domain/model';
import { conflictNames, DRAG_TYPE, TimeRange, useMoveTask } from './AgendaView';

/**
 * Semana completa: siete días con sus hábitos programados, tareas y fechas importantes.
 * Se puede arrastrar una tarea a otro día (con ratón) o tocarla para reprogramarla.
 */
export function WeekView({
  anchor,
  setAnchor,
  onMove,
  onOpenDay,
}: {
  anchor: LocalDate;
  setAnchor: (d: LocalDate) => void;
  onMove: (h: HabitRec) => void;
  onOpenDay: (d: LocalDate) => void;
}) {
  const { t, fmtDate, m } = useI18n();
  const { data, ctx, idx } = useApp();
  const move = useMoveTask();
  const [over, setOver] = useState<LocalDate | null>(null);
  const settings = useMemo(() => toEngineSettings(ctx.settings), [ctx.settings]);
  const start = weekStartOf(anchor, settings);
  const week = useMemo(
    () => buildWeek({ habits: data.habits, reminders: data.reminders, notes: data.notes, vacations: data.vacations, idx, settings, today: ctx.today, start }),
    [data.habits, data.reminders, data.notes, data.vacations, idx, settings, ctx.today, start],
  );
  const end = addDays(start, 6);
  const thisWeek = ctx.today >= start && ctx.today <= end;
  const conflicts = week.reduce((n, d) => n + d.agenda.conflicts.length, 0);
  const estimated = week.some((d) => d.entries.some((e) => e.estimated));

  const exportIcs = () => {
    const blob = new Blob([buildIcs(week, { name: m.app.name })], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `habitos-${start}.ics`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const drop = (day: LocalDate) => (e: DragEvent) => {
    e.preventDefault();
    setOver(null);
    const task = data.habits.find((h) => h.id === e.dataTransfer.getData(DRAG_TYPE));
    if (task && day >= ctx.today && task.dueDate !== day) void move(task, day, task.dueTime ?? null);
  };

  return (
    <>
      <nav className="row" aria-label={t('agenda.weekNav')} style={{ justifyContent: 'space-between' }}>
        <button type="button" className="icon-btn" aria-label={t('agenda.prevWeek')} onClick={() => setAnchor(addDays(start, -7))}>
          <ChevronLeft size={22} aria-hidden="true" />
        </button>
        <div style={{ textAlign: 'center' }}>
          <strong aria-live="polite">
            {thisWeek
              ? t('agenda.thisWeek')
              : t('agenda.weekRange', { from: fmtDate(start, { day: 'numeric', month: 'short' }), to: fmtDate(end, { day: 'numeric', month: 'short' }) })}
          </strong>
          <div className="muted xsmall">
            {t('agenda.summary', { pending: week.reduce((n, d) => n + d.agenda.pending, 0), done: week.reduce((n, d) => n + d.agenda.done, 0) })}
          </div>
        </div>
        <button type="button" className="icon-btn" aria-label={t('agenda.nextWeek')} onClick={() => setAnchor(addDays(start, 7))}>
          <ChevronRight size={22} aria-hidden="true" />
        </button>
      </nav>
      <div className="row" style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
        {!thisWeek ? (
          <button type="button" className="btn btn--ghost btn--sm" onClick={() => setAnchor(ctx.today)}>
            {t('agenda.backToThisWeek')}
          </button>
        ) : (
          <span />
        )}
        <button type="button" className="btn btn--ghost btn--sm" onClick={exportIcs}>
          <Download size={16} aria-hidden="true" /> {t('agenda.exportIcs')}
        </button>
      </div>
      {conflicts > 0 && (
        <p className="banner banner--warning small" role="note">
          <AlertTriangle size={18} aria-hidden="true" /> {t('agenda.conflictsSummary', { count: conflicts })}
        </p>
      )}

      <div className="week">
        {week.map(({ day, agenda, entries }) => {
          const clashes = conflictNames(agenda.conflicts, entries);
          const label = fmtDate(day, { weekday: 'long', day: 'numeric', month: 'long' });
          return (
            <section
              key={day}
              className={`week__day${day === ctx.today ? ' week__day--today' : ''}${day < ctx.today ? ' week__day--past' : ''}${over === day ? ' week__day--over' : ''}`}
              aria-label={label}
              onDragOver={(e) => {
                if (!e.dataTransfer.types.includes(DRAG_TYPE) || day < ctx.today) return;
                e.preventDefault();
                setOver(day);
              }}
              onDragLeave={() => setOver((o) => (o === day ? null : o))}
              onDrop={drop(day)}
            >
              <button type="button" className="week__head" onClick={() => onOpenDay(day)} aria-label={t('agenda.openDay', { day: label })}>
                <span className="week__weekday">{fmtDate(day, { weekday: 'short' })}</span>
                <span className="week__date num">{fmtDate(day, { day: 'numeric' })}</span>
                {agenda.pending > 0 && <span className="week__count num">{agenda.pending}</span>}
              </button>
              {entries.length === 0 ? (
                <p className="muted xsmall week__empty">{t('agenda.free')}</p>
              ) : (
                <ul className="week__list">
                  {entries.map((e) => (
                    <WeekItem key={e.key} entry={e} day={day} today={ctx.today} clashes={clashes.get(e.key)} onMove={onMove} />
                  ))}
                </ul>
              )}
            </section>
          );
        })}
      </div>
      {estimated && <p className="muted xsmall">{t('agenda.estimatedLegend')}</p>}
    </>
  );
}

function WeekItem({ entry: e, day, today, clashes, onMove }: { entry: AgendaEntry; day: LocalDate; today: LocalDate; clashes?: string[]; onMove: (h: HabitRec) => void }) {
  const { t } = useI18n();
  const cls = `week__item week__item--${e.kind}${e.done ? ' week__item--done' : ''}${clashes ? ' week__item--clash' : ''}`;
  const body = (
    <>
      <TimeRange entry={e} compact />
      <span className="week__title">{e.title}</span>
      {e.done && <span className="visually-hidden"> ({t('habit.status.completed')})</span>}
      {clashes && (
        <span className="week__clash">
          <AlertTriangle size={12} aria-hidden="true" /> {t('agenda.overlaps', { names: clashes.join(', ') })}
        </span>
      )}
    </>
  );
  if (e.kind === 'date') {
    return (
      <li className={cls}>
        <CalendarDays size={14} aria-hidden="true" /> {body}
      </li>
    );
  }
  if (e.kind === 'habit') {
    return (
      <li>
        <Link to={`/habits/${e.habit!.id}`} className={cls}>
          {body}
        </Link>
      </li>
    );
  }
  const movable = !e.done && day >= today;
  return (
    <li>
      <button
        type="button"
        className={cls}
        draggable={movable}
        onDragStart={(ev) => {
          ev.dataTransfer.setData(DRAG_TYPE, e.habit!.id);
          ev.dataTransfer.effectAllowed = 'move';
        }}
        onClick={() => !e.done && onMove(e.habit!)}
        aria-label={`${e.title}${e.time ? `, ${e.time}` : ''}. ${e.done ? t('habit.status.completed') : t('agenda.moveOther')}`}
      >
        {body}
      </button>
    </li>
  );
}
